import {useTransactionsLazyQuery} from '@transactions/graphql';
import * as React from 'react';
import {
  NavigationType,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';
import {amountArg} from 'src/components/AmountFilterSelect';
import Empty from 'src/ListView/Empty';
import ListContent from 'src/ListView/ListContent';
import {MoreButton} from 'src/ListView/MoreButton';
import {ScrollTable} from 'src/ScrollTable';
import {Header} from 'src/TransactionListView/Header';
import TransactionFilters, {
  getInitialFilters,
  saveFiltersToLocalStorage,
} from 'src/TransactionListView/TransactionFilters';
import TransactionRow from 'src/TransactionListView/TransactionRow';
import {TransactionSelectionBar} from 'src/TransactionListView/TransactionSelectionBar';
import {useSelectionState} from 'src/useSelectionState';
import type {UnifiedTransactionFragment} from '@transactions/graphql';

const localStorageKey = 'transactionFilters';

/**
 * The Transactions list: what the user recorded. The Ledger's Transactions
 * layer, which owns the page's breadcrumb.
 */
export const TransactionListView = () => {
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = getInitialFilters(searchParams, localStorageKey);
  const [filters, setFilters] = React.useState<TransactionFilters>(initialFilters);

  // A filter the user changes pushes a history entry; bringing the URL into
  // the filters' own form (on mount, or after the URL changed underneath)
  // replaces it, so Back never steps through copies of the same list — e.g.
  // after the Ledger switches to this layer.
  const handleUpdateFilters = (
    newFilters: TransactionFilters,
    {replace = false}: {replace?: boolean} = {},
  ) => {
    saveFiltersToLocalStorage(newFilters, localStorageKey);
    setSearchParams(newFilters.toQueryString(), {replace});
    setFilters(newFilters);
  };

  React.useEffect(() => {
    setSearchParams(initialFilters.toQueryString(), {replace: true});
  }, []);
  React.useEffect(() => {
    if (searchParams.toString()) {
      handleUpdateFilters(TransactionFilters.fromSearchParams(searchParams), {
        replace: true,
      });
    }
  }, [searchParams.toString()]);

  const [loadTransactions, {loading, error, data, fetchMore}] =
    useTransactionsLazyQuery({
      notifyOnNetworkStatusChange: true,
      // Back/forward re-renders the cached multi-page list instantly so
      // scroll restoration lands where the user left off; fresh navigations
      // still hit the network for up-to-date data.
      fetchPolicy:
        navigationType === NavigationType.Pop ? 'cache-first' : 'network-only',
    });
  const pageData = data?.transactions;
  const all = pageData?.items ?? [];
  const variables = {
    categories: filters.categories,
    tags: filters.tags,
    search: filters.search,
    fieldFilters: filters.fieldFilters,
    kinds: filters.kinds,
    startDate: filters.startDate,
    endDate: filters.endDate,
    // Filter state carries dollars; the query args want cents.
    minAmount: amountArg(filters.minAmount) ?? null,
    maxAmount: amountArg(filters.maxAmount) ?? null,
  };

  const selectionState = useSelectionState<UnifiedTransactionFragment>(
    all,
    [],
  );
  const {selected, setSelected, clearSelection} = selectionState;
  // Select mode is entered explicitly via the header button or implicitly by
  // long-pressing a row; the header's X (or Escape) exits both.
  const [explicitSelectMode, setExplicitSelectMode] = React.useState(false);

  const exitSelectMode = () => {
    setExplicitSelectMode(false);
    clearSelection();
  };

  React.useEffect(() => {
    exitSelectMode();
    loadTransactions({variables});
  }, [filters]);

  const handleMore = () => {
    if (!fetchMore || !pageData?.hasMore || !pageData.endCursor) return;
    // Page merging happens in the cache (see the transactions field policy
    // in ApolloProvider), so the accumulated list survives navigation.
    fetchMore({
      variables: {
        ...variables,
        beforeCursor: pageData.endCursor,
      },
    });
  };

  const isSelectMode = explicitSelectMode || selected.length > 0;

  const renderItem = (transaction: UnifiedTransactionFragment) => (
    <TransactionRow
      key={transaction._id}
      transaction={transaction}
      selected={selected.includes(transaction)}
      isSelectMode={isSelectMode}
      onSelect={setSelected(transaction)}
      onShiftSelect={() => selectionState.setShiftSelected(transaction)}
    />
  );

  return (
    <>
      <Header
        filters={filters}
        isSelectMode={isSelectMode}
        onUpdateFilters={handleUpdateFilters}
        onEnterSelectMode={() => setExplicitSelectMode(true)}
        onClearSelection={exitSelectMode}
      />
      <ScrollTable
        // fieldFilters affect the query but are omitted from toQueryString,
        // so the URL alone cannot distinguish field-filtered list states.
        scrollStateKey={
          filters.fieldFilters ? JSON.stringify(filters.fieldFilters) : ''
        }
        body={
          <>
            <ListContent
              loading={loading}
              error={error}
              data={all}
              ifEmpty={<Empty>No transactions</Empty>}
              renderItem={renderItem}
            />
            {all.length > 0 && (
              <MoreButton
                loading={loading}
                hasMore={pageData?.hasMore ?? false}
                onMore={handleMore}
              />
            )}
          </>
        }
      />
      <TransactionSelectionBar
        open={isSelectMode}
        selected={selected}
        onDone={exitSelectMode}
      />
    </>
  );
};
